import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { serverUrl } from '../authService';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

// Define the steps in order
export interface ChecklistStep {
    id: string;
    key: string;
    title: string;
    description: string;
    completionCriteria?: string;
}

export const CHECKLIST_STEPS: ChecklistStep[] = [
    {
        id: 'complete_first_query',
        key: 'complete_first_query',
        title: 'Complete First Query',
        description: 'Get your first research question answered',
        completionCriteria: 'Automatically completed when you receive your first query result'
    },
    {
        id: 'conduct_bibliometric_analysis',
        key: 'conduct_bibliometric_analysis',
        title: 'Conduct Bibliometric Analysis',
        description: 'Explore citation patterns and research trends',
        completionCriteria: 'Completed when you view bibliometric analysis of a query'
    },
    {
        id: 'share_result',
        key: 'share_result',
        title: 'Share Result',
        description: 'Share your research findings with colleagues',
        completionCriteria: 'Completed when you share a query result'
    },
    {
        id: 'upgrade_to_pro',
        key: 'upgrade_to_pro',
        title: 'Upgrade to Pro',
        description: 'Get unlimited access to all features',
        completionCriteria: 'Automatically completed when you subscribe to a paid plan'
    },
    {
        id: 'upload_documents',
        key: 'upload_documents',
        title: 'Upload Your Own Documents',
        description: 'Add your research papers to analyze',
        completionCriteria: 'Completed when you upload a document to your library'
    },
    {
        id: 'deep_research_query',
        key: 'deep_research_query',
        title: 'Do Deep Research Query',
        description: 'Perform an in-depth literature review',
        completionCriteria: 'Completed when you perform a query with Deep Research mode'
    },
    {
        id: 'extract_data',
        key: 'extract_data',
        title: 'Extract Data in Library',
        description: 'Extract insights from your library documents',
        completionCriteria: 'Completed when you use the Extract Data feature'
    },
    {
        id: 'create_diagram',
        key: 'create_diagram',
        title: 'Create a Diagram',
        description: 'Visualize your research with diagrams',
        completionCriteria: 'Completed when you create your first diagram'
    },
    {
        id: 'ai_editor',
        key: 'ai_editor',
        title: 'Use AI Writer',
        description: 'Create outlines, expand sections, add citations, and more with AI assistance',
        completionCriteria: 'Completed when you create or edit a document with the AI Editor'
    }
];

interface ChecklistContextType {
    completedSteps: string[];
    markStepComplete: (stepId: string) => Promise<void>;
    isStepCompleted: (stepId: string) => boolean;
    checkStepStatus: (stepId: string) => { completed: boolean; step: ChecklistStep | undefined; loading: boolean };
    getAllSteps: () => ChecklistStep[];
    getPendingSteps: () => ChecklistStep[];
    getCompletedSteps: () => ChecklistStep[];
    getProgress: () => number;
    isLoading: boolean;
    isDataFetched: boolean;
}

const ChecklistContext = createContext<ChecklistContextType | undefined>(undefined);

export const ChecklistProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [completedSteps, setCompletedSteps] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isDataFetched, setIsDataFetched] = useState<boolean>(false);
    const { user, loggedIn, isPro } = useAuth();

    useEffect(() => {
        if (loggedIn && user) {
            fetchCompletedSteps();
        } else {
            setIsLoading(false);
        }
    }, [loggedIn, user]);

    // Automatically mark "upgrade_to_pro" as completed if user is a pro member
    useEffect(() => {
        if (isPro && !isLoading && !completedSteps.includes('upgrade_to_pro')) {
            markStepComplete('upgrade_to_pro');
        }
    }, [isPro, isLoading, completedSteps]);

    const fetchCompletedSteps = async () => {
        if (isDataFetched) return;

        setIsLoading(true);
        try {
            const response = await axios.get(`${serverUrl}/user/checklist`, {
                withCredentials: true
            });
            console.log('Fetched checklist progress:', response.data);
            if (response.data.completed_steps) {
                setCompletedSteps(response.data.completed_steps);
            }
            setIsDataFetched(true);
        } catch (error) {
            console.error('Error fetching checklist progress:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const markStepComplete = async (stepId: string) => {
        if (completedSteps.includes(stepId)) {
            return; // Already completed
        }

        try {
            const response = await axios.post(
                `${serverUrl}/user/checklist/complete`,
                { step_key: stepId },
                { withCredentials: true }
            );

            if (response.status === 201) {
                setCompletedSteps(prev => [...prev, stepId]);
            }
        } catch (error) {
            console.error('Error marking step as complete:', error);
            toast.error('Could not update progress');
        }
    };

    const isStepCompleted = (stepId: string) => {
        return completedSteps.includes(stepId);
    };

    const checkStepStatus = (stepId: string) => {
        // If data isn't fetched yet and not currently loading, trigger fetch
        if (!isDataFetched && !isLoading && loggedIn && user) {
            fetchCompletedSteps();
        }


        const step = CHECKLIST_STEPS.find(step => step.id === stepId);
        const completed = isStepCompleted(stepId);
        return { completed, step, loading: isLoading };
    };

    const getAllSteps = () => {
        return CHECKLIST_STEPS;
    };

    const getPendingSteps = () => {
        return CHECKLIST_STEPS.filter(step => !completedSteps.includes(step.id));
    };

    const getCompletedSteps = () => {
        return CHECKLIST_STEPS.filter(step => completedSteps.includes(step.id));
    };

    const getProgress = () => {
        if (CHECKLIST_STEPS.length === 0) return 0;
        return (completedSteps.length / CHECKLIST_STEPS.length) * 100;
    };

    return (
        <ChecklistContext.Provider value={{
            completedSteps,
            markStepComplete,
            isStepCompleted,
            checkStepStatus,
            getAllSteps,
            getPendingSteps,
            getCompletedSteps,
            getProgress,
            isLoading,
            isDataFetched
        }}>
            {children}
        </ChecklistContext.Provider>
    );
};

export const useChecklist = () => {
    const context = useContext(ChecklistContext);
    if (context === undefined) {
        throw new Error('useChecklist must be used within a ChecklistProvider');
    }
    return context;
}; 